import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router'
import {
  bedragInEuro,
  BetaallinkOnbekend,
  haalBetaalstand,
  type BetaalStand,
} from '../lib/api'

type Toestand =
  | { soort: 'laden' }
  | { soort: 'gereed'; stand: BetaalStand }
  | { soort: 'onbekend' }
  | { soort: 'fout' }

/*
 * De blijvende betaallink, /betaal/{token} (FR-11, ADR-0006).
 *
 * Deze pagina bestaat omdat een Mollie-checkout bij één betaling hoort en
 * verloopt. Zo'n URL in een e-mail zetten betekent dat de cliënt er twee dagen
 * later op klikt en "verlopen" te zien krijgt — precies op het moment dat hij
 * alsnog wil betalen.
 *
 * Dit is de enige route die niet zonder JavaScript werkt. Dat kan ook niet: het
 * antwoord hangt af van een aanvraag die alleen de server kent. Hij staat
 * daarom op noindex en is via geen enkele link op de site te bereiken.
 *
 * Er wordt bewust niet automatisch doorgestuurd naar Mollie. De bezoeker ziet
 * eerst het bedrag en zijn kenmerk, en klikt zelf — een betaalpagina die uit
 * zichzelf begint te navigeren is verwarrend, en art. 6:230v BW wil dat de
 * totaalprijs kenbaar is vóórdat de bestelling wordt geplaatst.
 */
export default function Betaal() {
  const { token } = useParams<{ token: string }>()
  const [toestand, setToestand] = useState<Toestand>({ soort: 'laden' })

  useEffect(() => {
    if (!token) {
      setToestand({ soort: 'onbekend' })
      return
    }

    const afbreken = new AbortController()

    haalBetaalstand(token, afbreken.signal)
      .then((stand) => setToestand({ soort: 'gereed', stand }))
      .catch((fout: unknown) => {
        if (afbreken.signal.aborted) return
        setToestand(
          fout instanceof BetaallinkOnbekend
            ? { soort: 'onbekend' }
            : { soort: 'fout' },
        )
      })

    return () => afbreken.abort()
  }, [token])

  return (
    <section className="section" style={{ paddingTop: 'clamp(36px,5vw,56px)' }}>
      <div className="wrap">
        <div className="flow">
          <div className="flowcard">
            <Inhoud toestand={toestand} />
          </div>
        </div>
      </div>
    </section>
  )
}

function Inhoud({ toestand }: { toestand: Toestand }) {
  if (toestand.soort === 'laden') {
    // aria-live zodat een schermlezer de uitkomst hoort zodra die er is.
    return (
      <div aria-live="polite" aria-busy="true">
        <h1>Een moment geduld</h1>
        <p className="lede">Wij halen de gegevens van uw aanvraag op.</p>
      </div>
    )
  }

  if (toestand.soort === 'onbekend') {
    return (
      <div aria-live="polite">
        <h1>Deze betaallink werkt niet</h1>
        <p className="lede">
          Mogelijk is de link niet volledig meegekomen uit uw e-mail. Kopieer
          hem in zijn geheel, of{' '}
          <Link to="/contact">neem contact met ons op</Link> — dan sturen wij u
          een nieuwe.
        </p>
      </div>
    )
  }

  if (toestand.soort === 'fout') {
    return (
      <div aria-live="polite">
        <h1>Het lukt nu even niet</h1>
        <p className="lede">
          Wij konden uw gegevens niet ophalen. Probeer het over een paar minuten
          opnieuw. Blijft het misgaan?{' '}
          <Link to="/contact">Laat het ons weten</Link>.
        </p>
      </div>
    )
  }

  const { stand } = toestand

  if (stand.status === 'betaald') {
    // Geen tweede betaalknop. Dubbel incasseren is erger dan een cliënt die
    // even niet begrijpt waarom de knop weg is (FR-11).
    return (
      <div aria-live="polite">
        <h1>Deze aanvraag is al betaald</h1>
        <p className="lede">
          Wij hebben {bedragInEuro(stand.bedragCenten)} ontvangen voor kenmerk{' '}
          <strong>{stand.kenmerk}</strong>. U hoeft niets meer te doen.
        </p>
        <p className="lede" style={{ marginTop: '14px' }}>
          Wij nemen contact met u op om een afspraakmoment te plannen.
        </p>
        <div style={{ marginTop: '26px' }}>
          <Link to="/" className="btn btn--ghost">
            Terug naar de homepage
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div aria-live="polite">
      <h1>Rond uw betaling af</h1>
      <p className="lede">
        Uw aanvraag staat klaar. Na de betaling nemen wij contact met u op om
        een afspraakmoment te plannen.
      </p>

      <div className="paybox">
        <div>
          <b>GPK-beoordeling</b>
          <br />
          <span style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>
            Kenmerk: {stand.kenmerk}
          </span>
        </div>
        <span className="amount">{bedragInEuro(stand.bedragCenten)}</span>
      </div>

      {stand.checkoutUrl && (
        <a href={stand.checkoutUrl} className="btn btn--coral btn--big">
          Betaal met iDEAL
        </a>
      )}
    </div>
  )
}
